import { useApolloClient, useMutation } from '@apollo/client';
import { captureEvent } from '@sentry/react';
import { GET_VIDEO_UPLOAD_LINK, UPDATE_VIDEO_UPLOAD_STATUS } from 'graphql/secure/mutations';
import { GET_VIDEOS } from 'graphql/secure/queries';
import { useCallback, useState } from 'react';
import { EVideoUploadStatus } from 'FeatureModules/Media/types/media.types';
import { urlSafeFileName } from 'utils/urlSafeFileName.utils';

const useVideoUpload = () => {
  const client = useApolloClient();
  const [state, setState] = useState({
    uploadTask: null,
    record: null,
    error: null,
  });

  const [getUploadLink] = useMutation(GET_VIDEO_UPLOAD_LINK, {
    fetchPolicy: 'no-cache',
  });

  const [setUploadStatus] = useMutation(UPDATE_VIDEO_UPLOAD_STATUS, {
    refetchQueries: [
      {
        query: GET_VIDEOS,
      },
    ],
  });

  const uploadFile = useCallback(
    async ({
      file,
      onError = () => null,
      onSuccess = () => null,
      onProgress = () => null,
      onRecordCreated = () => null,
    }) => {
      try {
        const fileName = urlSafeFileName(file.name);

        const result = await getUploadLink({
          variables: {
            input: {
              fileName: fileName,
              fileSize: file.size,
            },
          },
        });

        const {
          video: { id },
          uploadLink,
        } = result.data.videoUploadLink;

        onRecordCreated({ id });

        const storageRef = client.defaultOptions.firebase
          .getFirebase()
          .storage()
          .refFromURL(uploadLink);

        const uploadTask = storageRef.put(file);

        uploadTask.on(
          'state_changed',
          ({ bytesTransferred, totalBytes }) => {
            const progress = parseInt(((bytesTransferred / totalBytes) * 100).toFixed(2));

            onProgress(progress);
          },
          // error handler
          () => {
            setUploadStatus({
              variables: {
                id,
                input: {
                  uploadStatus: EVideoUploadStatus.FAILED,
                },
              },
            });
            onError();
          },
          // success handler
          () => {
            setUploadStatus({
              variables: {
                id,
                input: {
                  uploadStatus: EVideoUploadStatus.SUCCESS,
                },
              },
            });

            onSuccess();
          }
        );

        const newState = {
          ...state,
          uploadTask,
        };

        setState(newState);
        return newState;
      } catch (error) {
        captureEvent(error);
        const newState = {
          ...state,
          error: error.message,
        };

        setState(newState);
        onError();
        return newState;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return [uploadFile, { ...state }];
};

export default useVideoUpload;
